import { platformClient } from './platform.client';
import {
  Class,
  CreateStudentRequest,
  LinkCodeResponse,
  PatchSchoolFeatureFlagsRequest,
  PatchSchoolFeatureFlagsResponse,
  School,
  Student,
} from '../types';
import { StatusResponse } from '../../../types/api';

export const getSchool = async (schoolId: string): Promise<School> => {
  const response = await platformClient.get<School>(`/schools/${schoolId}`);
  return response.data;
};

export const updateSchool = async (
  schoolId: string,
  data: Partial<School>
): Promise<StatusResponse> => {
  const response = await platformClient.patch<StatusResponse>(`/schools/${schoolId}`, data);
  return response.data;
};

/** Dev Team: full `feature_flags` snapshot in body. Response may be partial — callers should refetch GET /schools/:id if they need a full School. */
export const patchSchoolFeatureFlags = async (
  schoolId: string,
  body: PatchSchoolFeatureFlagsRequest
): Promise<PatchSchoolFeatureFlagsResponse> => {
  const response = await platformClient.patch<PatchSchoolFeatureFlagsResponse>(
    `/schools/${schoolId}/feature-flags`,
    body
  );
  return response.data;
};

export const listClasses = async (schoolId: string): Promise<Class[]> => {
  const response = await platformClient.get<Class[]>(`/schools/${schoolId}/classes`);
  return response.data;
};

export interface CreateClassRequest {
  name: string;
  grade: string;
  section?: string;
  academic_year?: string;
  status?: string;
}

export const createClass = async (schoolId: string, data: CreateClassRequest): Promise<Class> => {
  const response = await platformClient.post<Class>(`/schools/${schoolId}/classes`, data);
  return response.data;
};

export const createStudent = async (
  schoolId: string,
  data: CreateStudentRequest
): Promise<Student> => {
  const response = await platformClient.post<Student>(`/schools/${schoolId}/students`, data);
  return response.data;
};

export const listStudents = async (schoolId: string): Promise<Student[]> => {
  const response = await platformClient.get<Student[]>(`/schools/${schoolId}/students`);
  return response.data;
};

export const inviteTeacher = async (
  schoolId: string,
  email: string
): Promise<StatusResponse> => {
  const response = await platformClient.post<StatusResponse>(
    `/schools/${schoolId}/invites/teachers`,
    { email }
  );
  return response.data;
};

export const inviteParent = async (
  schoolId: string,
  email: string,
  studentId?: string
): Promise<StatusResponse> => {
  const response = await platformClient.post<StatusResponse>(
    `/schools/${schoolId}/invites/parents`,
    { email, student_id: studentId }
  );
  return response.data;
};

export const inviteStudent = async (
  schoolId: string,
  email: string,
  studentId?: string
): Promise<StatusResponse> => {
  const response = await platformClient.post<StatusResponse>(
    `/schools/${schoolId}/invites/students`,
    { email, student_id: studentId }
  );
  return response.data;
};

export const generateLinkCode = async (
  schoolId: string,
  studentId: string
): Promise<LinkCodeResponse> => {
  const response = await platformClient.post<LinkCodeResponse>(
    `/schools/${schoolId}/students/${studentId}/link-code`
  );
  return response.data;
};

export const linkParentStudent = async (
  schoolId: string,
  data: {
    parent_user_id: string;
    student_id: string;
    relationship?: string;
  }
): Promise<StatusResponse> => {
  const response = await platformClient.post<StatusResponse>(
    `/schools/${schoolId}/link-parent-student`,
    data
  );
  return response.data;
};

/** Student-facing: /me/school-language wrapper */
export interface StudentSchoolLanguage {
  ui_default: string;
  allowed: string[];
  preferred_dialect_keys: string[];
  default_dialect_key: string;
}

export const getMySchoolLanguage = async (): Promise<StudentSchoolLanguage> => {
  const response = await platformClient.get<StudentSchoolLanguage>('/me/school-language');
  return response.data;
};

/** Optional: GET /me/school-dialects (if backend provides). Returns ordered dialect metadata for students. */
export interface StudentSchoolDialect {
  key: string;
  name: string;
  language?: string | null;
  region?: string | null;
  category?: string | null;
}

export interface StudentSchoolDialectsResponse {
  default_dialect_key: string;
  dialects: StudentSchoolDialect[];
}

export const getMySchoolDialects = async (): Promise<StudentSchoolDialectsResponse> => {
  const response = await platformClient.get<StudentSchoolDialectsResponse>('/me/school-dialects');
  return response.data;
};

/** Patch only preferred dialect keys for a school (Dev Team). Sends only { language: { preferred_dialect_keys: [...] } } */
export const patchSchoolLanguage = async (
  schoolId: string,
  payload: { language: { preferred_dialect_keys: string[] } }
): Promise<School> => {
  const response = await platformClient.patch<School>(`/schools/${schoolId}/language`, payload);
  return response.data;
};

